import { Backend, PrimitiveType } from './workflow.type';

import { ErrCallback, FlatData, StorageBackend } from './storage-backend.base';
import { StatefulService } from '@app/service/stateful.service';

const SESSION_STORAGE_KEY = 'cuASSD';

/**
 * Backend for storing data on session storage
 */
export class SessionStorageBackend extends StorageBackend {
  public readonly type: Backend = 'ss';

  constructor(
    protocol: string,
    data: FlatData,
    arraySizes: { [rootPath: string]: number },
    endpointPath: string,
    token: string,
    statefulService: StatefulService // ✅ Ensure this argument is expected
  ) {
    super(protocol, data, arraySizes, endpointPath, token, statefulService); // ✅ Pass it to parent
    // this.logger.info(`SessionStorageBackend received StatefulService:`, statefulService);
  }

  /**
   * @inheritdoc
   */
  public load(path: string, cb: (err?: Error, data?: PrimitiveType) => void): void {
    const raw = sessionStorage.getItem(this.itemKey) || '{}';
    const json = JSON.parse(raw);
    this.syncDump(json);
    cb(undefined, this.data[`${this.protocol}:${path}`]);
    this.propagateBundle.next();
  }

  /**
   * @inheritdoc
   */
  public save(path: string, value: PrimitiveType, cb: ErrCallback): void {
    const raw = sessionStorage.getItem(this.itemKey) || '{}';
    const json = JSON.parse(raw);
    this.syncDump(json);
    this.data[`${this.protocol}:${path}`] = value;
    this.updateArraySizes.next(null);
    const data: FlatData = {};
    for (const key of Object.keys(this.data)) {
      const [protocol, dataPath] = key.split(':');
      if (protocol === this.protocol) {
        data[dataPath] = this.data[key];
      }
    }
    sessionStorage.setItem(this.itemKey, JSON.stringify(data));
    cb();
    this.propagateBundle.next();
  }

  /**
   * @inheritdoc
   */
  public init() {
    this.initialized = true;
  }

  /**
   * clear data from backend
   */
  public clear(): void {
    sessionStorage.removeItem(this.itemKey);

    Object.keys(this.data).forEach((key) => {
      delete this.data[key];
    });
    this.initialized = false;
  }

  /**
   * Key name for session storage instance
   */
  private get itemKey(): string {
    return `${SESSION_STORAGE_KEY}:${this.protocol}`;
  }
}
